import { useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { aiGenerateCourse, createBlankCourse } from "@/lib/courses";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Sparkles, UserCog, Paperclip, X, FileText } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function NewCourseWizard() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const mode = location.pathname.endsWith("/blank") ? "blank" : "ai";
  const targetTeacherId = sp.get("as_teacher") || "";
  const isAdminProxy = user?.role === "admin" && !!targetTeacherId;

  const [form, setForm] = useState({
    topic: "",
    audience: "",
    level: "Beginner",
    num_chapters: 3,
    extra_instructions: "",
    title: "",
  });
  const [loading, setLoading] = useState(false);
  // Phase 23 — reference documents attached to the AI generation.
  const [files, setFiles] = useState([]); // File[]

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const MAX_FILES = 5;
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_EXT = [".pdf", ".docx"];
  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const kept = [];
    for (const f of incoming) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${f.name}: only PDF or DOCX allowed`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name}: over 10 MB limit`);
        continue;
      }
      kept.push(f);
    }
    setFiles((cur) => {
      const merged = [...cur, ...kept];
      if (merged.length > MAX_FILES) {
        toast.error(`Only ${MAX_FILES} files allowed — extras skipped`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };
  const removeFile = (idx) =>
    setFiles((cur) => cur.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let course;
      let courseId;
      let isAsyncJob = false;
      if (mode === "blank") {
        course = await createBlankCourse({
          title: form.title,
          level: form.level,
        });
        courseId = course.id;
        toast.success("Blank course created");
      } else {
        course = await aiGenerateCourse(
          {
            topic: form.topic,
            audience: form.audience,
            level: form.level,
            num_chapters: Number(form.num_chapters),
            extra_instructions: form.extra_instructions,
            ...(isAdminProxy ? { target_teacher_id: targetTeacherId } : {}),
          },
          files,
        );
        // New async shape: {course_id, job_id, status:'queued'}
        // Legacy sync shape: full Course with .id and .chapters
        if (course.job_id) {
          isAsyncJob = true;
          courseId = course.course_id;
          if (course.source_documents?.length) {
            toast.success(
              `Reference documents processed (${course.source_documents.length}) — drafting your course…`
            );
          } else {
            toast.success("Drafting your course… (~30–60s). You'll see it appear shortly.");
          }
        } else {
          courseId = course.id;
          toast.success(`Generated ${course.chapters.length} chapters`);
        }
      }
      if (isAdminProxy) {
        navigate("/admin", { replace: true });
      } else {
        navigate(`/teacher/courses/${courseId}/edit`, { replace: true });
      }
    } catch (err) {
      const d = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="new-course-wizard">
      <DashboardHeader
        subtitle={mode === "ai" ? "Teacher · Generate course" : "Teacher · Blank course"}
      />
      <main className="mx-auto max-w-2xl px-6 py-10">
        <BackButton to={isAdminProxy ? "/admin" : "/teacher"} className="mb-2" />
        {isAdminProxy && (
          <div
            className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-2 text-sm text-amber-900"
            data-testid="admin-proxy-banner"
          >
            <UserCog className="h-4 w-4" />
            <span>
              Admin mode — creating this course on behalf of teacher{" "}
              <Badge variant="outline" className="ml-1 font-mono text-xs">
                {targetTeacherId.slice(0, 8)}…
              </Badge>
            </span>
          </div>
        )}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-4 w-4 text-amber-600" />
              {mode === "ai" ? "Generate course with AI" : "Create blank course"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mode === "ai" ? (
              <form className="space-y-4" onSubmit={submit}>
                <div className="space-y-2">
                  <Label htmlFor="topic">Topic</Label>
                  <Input
                    id="topic"
                    value={form.topic}
                    onChange={(e) => update("topic", e.target.value)}
                    placeholder="e.g. Indian History 1857–1947"
                    required
                    data-testid="wizard-topic"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Level</Label>
                    <Select value={form.level} onValueChange={(v) => update("level", v)}>
                      <SelectTrigger data-testid="wizard-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((l) => (
                          <SelectItem key={l} value={l}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="num">Chapters</Label>
                    <Input
                      id="num"
                      type="number"
                      min={1}
                      max={8}
                      value={form.num_chapters}
                      onChange={(e) => update("num_chapters", e.target.value)}
                      data-testid="wizard-num-chapters"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aud">Audience</Label>
                  <Input
                    id="aud"
                    value={form.audience}
                    onChange={(e) => update("audience", e.target.value)}
                    placeholder="e.g. Grade 9 students"
                    required
                    data-testid="wizard-audience"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="extra">Extra instructions</Label>
                  <Textarea
                    id="extra"
                    rows={3}
                    value={form.extra_instructions}
                    onChange={(e) => update("extra_instructions", e.target.value)}
                    placeholder="Curriculum notes, style preferences…"
                    data-testid="wizard-extra"
                  />
                </div>

                {/* Phase 23 — reference documents */}
                <div
                  className="space-y-2"
                  data-testid="wizard-reference-docs-section"
                >
                  <Label>Reference documents (optional)</Label>
                  <label
                    className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-stone-300 bg-stone-50 py-4 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 transition-colors"
                    data-testid="wizard-file-drop-zone"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      addFiles(e.dataTransfer.files);
                    }}
                  >
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.docx"
                      onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = "";
                      }}
                      data-testid="wizard-file-input"
                    />
                    <div className="flex items-center gap-2 text-sm text-stone-700">
                      <Paperclip className="h-4 w-4 text-amber-600" />
                      <span>Attach PDF or DOCX ({files.length}/{MAX_FILES})</span>
                    </div>
                    <div className="text-xs text-stone-500">
                      We&apos;ll use these to guide chapter generation. Text is
                      stored on the course for later regeneration.
                    </div>
                  </label>
                  {files.length > 0 && (
                    <ul className="space-y-1 mt-2">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${i}`}
                          className="flex items-center justify-between rounded border border-stone-200 bg-white px-3 py-1.5 text-xs"
                          data-testid={`wizard-file-row-${i}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                            <span className="truncate">{f.name}</span>
                            <span className="text-stone-400 shrink-0">
                              ({Math.round(f.size / 1024)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            className="text-stone-400 hover:text-red-600"
                            data-testid={`wizard-file-remove-${i}`}
                            aria-label={`Remove ${f.name}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                  data-testid="wizard-generate-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating… (30–60s)
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" /> Generate course
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form className="space-y-4" onSubmit={submit}>
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => update("title", e.target.value)}
                    required
                    data-testid="wizard-blank-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Level</Label>
                  <Select value={form.level} onValueChange={(v) => update("level", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                  data-testid="wizard-create-blank-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
